/**
 * src/engine/capGainsBrackets.ts — bracketed long-term capital-gains tax (§6.12b).
 *
 * The flat-rate model (`combinedCapGainsRate`) applies one rate to every dollar of gain.
 * That is a fair approximation of a MARGINAL rate, but it cannot express two real effects:
 *
 *   1. The long-term rate depends on TOTAL taxable income INCLUDING the gain itself, so a
 *      large realization walks itself through the 0% → 15% → 20% boundaries within one
 *      tax year (IRS Topic 409).
 *   2. The 3.8% net investment income tax is a step at a FIXED, never-indexed MAGI
 *      threshold (IRC §1411), not a ramp.
 *
 * Both matter most in the case the flat model is least safe about: a forced liquidation,
 * which realizes a large gain in a single year and understates the pain of a margin call.
 *
 * Everything here is pure. Rates are percentages; thresholds are dollars; the returned
 * rate is a FRACTION (0.238, not 23.8).
 *
 * All gains are treated as long-term — deliberate, since the buy-borrow-die case this app
 * models is long-held appreciated assets. Short-term treatment is out of scope (§5).
 */
import type { CapGainsBracketTable, TaxFields } from '@/types';

/** The three thresholds that shape one taxpayer's gains tax, after any user override. */
export interface ResolvedBrackets {
  /** Taxable income at or below which long-term gains are taxed at 0%. */
  zeroMax: number;
  /** Taxable income at or below which long-term gains are taxed at 15%; above it, 20%. */
  fifteenMax: number;
  /** MAGI above which the NIIT applies. */
  niitThreshold: number;
  /** NIIT rate as a fraction (0.038). */
  niitRate: number;
  /** State rate as a fraction, applied to every dollar of gain regardless of bracket. */
  stateRate: number;
}

/**
 * Resolve the brackets for a scenario: table values for the filing status, with any
 * user override winning. A null override means "use the stamped table" (§5).
 *
 * `indexFactor` inflates the two FEDERAL thresholds for a projection year — the IRS
 * re-indexes them annually, so holding 2026 numbers fixed across a 30-year horizon
 * invents bracket creep that would not happen, taxing the sell path too heavily and
 * flattering the borrow case. The NIIT threshold is deliberately NOT indexed here: under
 * IRC §1411 it is fixed in nominal terms and has not moved since 2013, so freezing it is
 * what the law actually does.
 */
export function resolveBrackets(
  tax: TaxFields,
  table: CapGainsBracketTable,
  indexFactor = 1,
): ResolvedBrackets {
  const row = table.statuses[tax.filingStatus];
  const f = indexFactor > 0 ? indexFactor : 1;
  return {
    zeroMax: (tax.bracketZeroMaxOverride ?? row.maxZeroRateAmount) * f,
    fifteenMax: (tax.bracketFifteenMaxOverride ?? row.maxFifteenRateAmount) * f,
    niitThreshold: tax.niitThresholdOverride ?? row.niitThreshold,
    niitRate: table.niitRatePct / 100,
    stateRate: tax.stateCapGainsRatePct / 100,
  };
}

/**
 * Running position within one tax year. `realizedGain` accumulates across every sale in
 * the year — routine sell-path sales and forced liquidations alike — because the IRS
 * brackets do not care which caused the gain.
 */
export interface TaxYearPosition {
  /** Calendar year this position tracks; sales in a new year reset it. */
  year: number;
  /** Long-term gain realized so far this year. */
  realizedGain: number;
}

export function newTaxYearPosition(year: number): TaxYearPosition {
  return { year, realizedGain: 0 };
}

/** The federal long-term rate (fraction) that applies at a given taxable-income level. */
export function federalRateAt(taxableIncome: number, b: ResolvedBrackets): number {
  if (taxableIncome < b.zeroMax) return 0;
  if (taxableIncome < b.fifteenMax) return 0.15;
  return 0.2;
}

/**
 * The all-in marginal rate (fraction) on the NEXT dollar of gain, given how much gain has
 * already been realized this year. Federal bracket + NIIT (if MAGI is over the threshold)
 * + state.
 */
export function marginalRateAt(gainSoFar: number, tax: TaxFields, b: ResolvedBrackets): number {
  const taxableIncome = tax.otherTaxableIncome + gainSoFar;
  const magi = tax.otherMagi + gainSoFar;
  const niit = magi >= b.niitThreshold ? b.niitRate : 0;
  return federalRateAt(taxableIncome, b) + niit + b.stateRate;
}

/**
 * The gain amounts at which the marginal rate steps up, expressed as gain-so-far values
 * and returned ascending. Each boundary is a threshold minus the income already there;
 * boundaries at or below the current position are dropped (already crossed).
 */
export function rateStepBoundaries(tax: TaxFields, b: ResolvedBrackets): number[] {
  const steps = [
    b.zeroMax - tax.otherTaxableIncome,
    b.fifteenMax - tax.otherTaxableIncome,
    b.niitThreshold - tax.otherMagi,
  ].filter((g) => g > 0);
  return [...new Set(steps)].sort((x, y) => x - y);
}

export interface BracketedSale {
  /** Gross proceeds required. */
  grossSale: number;
  /** Gain realized by that sale. */
  gain: number;
  /** Tax owed on that gain. */
  tax: number;
  /** True when the sale could not be funded at any size (rate ≥ 100% of the gain). */
  denomInvalid: boolean;
}

/**
 * Gross-up to net `net` from an asset whose gain fraction is `g`, walking bracket
 * boundaries in closed form (§6.12b).
 *
 * Within one bracket the arithmetic is the flat-rate case: netting `n` needs
 * `S = n / (1 − g × r)`. The only wrinkle is that a large sale may exhaust the bracket
 * partway through, so each segment is capped at the gain that fits below the next
 * boundary and the remainder continues at the higher rate. Exact, and no iteration —
 * which matters because the naive formulation is circular: the rate depends on the gain,
 * which depends on the gross-up, which depends on the rate.
 *
 * `available` caps the gross proceeds (a bucket cannot sell more than it holds).
 * Mutates nothing; the caller advances `position` with the returned gain.
 */
export function grossUpBracketed(
  net: number,
  g: number,
  available: number,
  tax: TaxFields,
  b: ResolvedBrackets,
  position: TaxYearPosition,
): BracketedSale {
  if (net <= 0 || available <= 0) {
    return { grossSale: 0, gain: 0, tax: 0, denomInvalid: false };
  }
  // A zero-gain asset is untaxed: proceeds are the net, capped by what is there.
  if (g <= 0) {
    const s = Math.min(net, available);
    return { grossSale: s, gain: 0, tax: 0, denomInvalid: false };
  }

  const boundaries = rateStepBoundaries(tax, b);
  let remainingNet = net;
  let remainingAvailable = available;
  let gainSoFar = position.realizedGain;
  let grossSale = 0;
  let gain = 0;
  let taxOwed = 0;

  // Walk each rate segment, then a final unbounded segment at the top rate.
  const segmentCaps = [...boundaries.filter((x) => x > gainSoFar), Infinity];
  for (const cap of segmentCaps) {
    if (remainingNet <= 0 || remainingAvailable <= 0) break;
    const rate = marginalRateAt(gainSoFar, tax, b);
    const netFactor = 1 - g * rate;
    if (netFactor <= 0) {
      // Tax meets or exceeds the proceeds: no sale size can fund the draw.
      return { grossSale, gain, tax: taxOwed, denomInvalid: true };
    }
    // Gross proceeds that would exactly reach this segment's cap.
    const grossToCap = cap === Infinity ? Infinity : (cap - gainSoFar) / g;
    const grossForNet = remainingNet / netFactor;
    const grossThisSegment = Math.min(grossToCap, grossForNet, remainingAvailable);
    const gainThisSegment = grossThisSegment * g;
    grossSale += grossThisSegment;
    gain += gainThisSegment;
    taxOwed += gainThisSegment * rate;
    gainSoFar += gainThisSegment;
    remainingNet -= grossThisSegment * netFactor;
    remainingAvailable -= grossThisSegment;
  }

  return { grossSale, gain, tax: taxOwed, denomInvalid: false };
}

/**
 * Tax on a gain realized all at once (a forced liquidation, or the hypothetical full
 * liquidation behind the estate step-up figure), walking the same boundaries. Unlike the
 * gross-up the amount is known, so this integrates the rate over the gain directly.
 */
export function taxOnGain(
  gainAmount: number,
  tax: TaxFields,
  b: ResolvedBrackets,
  position: TaxYearPosition,
): number {
  if (gainAmount <= 0) return 0;
  const boundaries = rateStepBoundaries(tax, b);
  let gainSoFar = position.realizedGain;
  let remaining = gainAmount;
  let owed = 0;
  const segmentCaps = [...boundaries.filter((x) => x > gainSoFar), Infinity];
  for (const cap of segmentCaps) {
    if (remaining <= 0) break;
    const rate = marginalRateAt(gainSoFar, tax, b);
    const slice = cap === Infinity ? remaining : Math.min(remaining, cap - gainSoFar);
    owed += slice * rate;
    gainSoFar += slice;
    remaining -= slice;
  }
  return owed;
}

/**
 * The staleness gate (§7.3). The bracket table is hand-maintained from an annual IRS
 * Revenue Procedure and there is no machine-readable feed, so nothing but a check that
 * BITES keeps it honest. `currentYear` is injected so the gate itself is testable — a
 * check that has never been seen to fail is not a check.
 */
export function isBracketTableStale(table: CapGainsBracketTable, currentYear: number): boolean {
  return table.taxYear < currentYear;
}
